import { useState, useMemo, useRef, useEffect } from 'react';
import { Link, useOutletContext, useNavigate } from 'react-router-dom';
import { Plus, FileText, Search, Filter, Download, ChevronDown, Upload } from 'lucide-react';
import { useSpecs, useDeleteSpec } from '../hooks/useSpecifications';
import { exportAllMarkdown } from '../services/specificationService';
import toast from 'react-hot-toast';
import Spinner from '../components/ui/Spinner';
import SpecImportModal from '../components/spec/SpecImportModal';
import SingleSelectDropdown from '../components/common/SingleSelectDropdown';
import type { ProjectDetail } from '../services/projectService';

const STATUS_LABEL: Record<string, string> = {
  DRAFT: '초안',
  IN_REVIEW: '검토 중',
  APPROVED: '승인됨',
  IMPLEMENTED: '구현됨',
};

const STATUS_COLOR: Record<string, string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  IN_REVIEW: 'bg-yellow-100 text-yellow-800',
  APPROVED: 'bg-green-100 text-green-800',
  IMPLEMENTED: 'bg-blue-100 text-blue-800',
};

export default function SpecListPage() {
  const { project } = useOutletContext<{ project: ProjectDetail }>();
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const exportMenuRef = useRef<HTMLDivElement>(null);

  // 드롭다운 외부 클릭 닫기
  useEffect(() => {
    if (!showExportMenu) return;
    function handleClickOutside(e: MouseEvent) {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target as Node)) {
        setShowExportMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showExportMenu]);

  const handleExportAll = async (status?: string) => {
    setShowExportMenu(false);
    setIsExporting(true);
    try {
      await exportAllMarkdown(project.id, status);
      toast.success('마크다운 파일이 다운로드되었습니다.');
    } catch {
      toast.error('마크다운 추출에 실패했습니다.');
    } finally {
      setIsExporting(false);
    }
  };

  const { data: specs, isLoading } = useSpecs(project.id, {
    status: statusFilter || undefined,
  });
  const deleteMutation = useDeleteSpec(project.id);

  const filtered = useMemo(() => {
    if (!specs) return [];
    if (!searchQuery.trim()) return specs;
    const q = searchQuery.toLowerCase();
    return specs.filter(
      (s) =>
        s.title.toLowerCase().includes(q) ||
        s.functionCode.toLowerCase().includes(q) ||
        s.requirement.reqId.toLowerCase().includes(q),
    );
  }, [specs, searchQuery]);

  const handleDelete = async (specId: string, title: string) => {
    if (!confirm(`"${title}" 명세를 삭제하시겠습니까?`)) return;
    try {
      await deleteMutation.mutateAsync(specId);
      toast.success('기능 명세가 삭제되었습니다.');
    } catch {
      toast.error('삭제에 실패했습니다.');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner className="size-8" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-xl font-bold text-gray-900">명세</h1>
          <p className="text-sm text-gray-500 mt-0.5">{filtered.length}개 명세</p>
        </div>
        <div className="flex items-center gap-2">
          {/* 전체 추출 드롭다운 */}
          <div className="relative" ref={exportMenuRef}>
            <button
              type="button"
              onClick={() => setShowExportMenu(!showExportMenu)}
              disabled={isExporting}
              className="inline-flex items-center gap-1.5 px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
            >
              {isExporting ? <Spinner className="size-4" /> : <Download className="size-4" />}
              전체 추출
              <ChevronDown className="size-3.5" />
            </button>
            {showExportMenu && (
              <div className="absolute right-0 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1">
                <button
                  onClick={() => handleExportAll('ALL')}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  전체 (모든 상태)
                </button>
                <button
                  onClick={() => handleExportAll('APPROVED')}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  APPROVED만
                </button>
                <button
                  onClick={() => handleExportAll('IN_REVIEW_ABOVE')}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  IN_REVIEW 이상
                </button>
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => setShowImportModal(true)}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-gray-700"
          >
            <Upload className="size-4" />
            Import
          </button>
          <Link
            to={`/projects/${project.projectKey}/specs/new`}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors shadow-sm"
          >
            <Plus className="size-4" />새 명세
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-gray-400" />
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="제목, 기능코드, 요구사항 검색..."
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
          />
        </div>
        <div className="flex items-center gap-1.5">
          <Filter className="size-4 text-gray-400" />
          <SingleSelectDropdown
            label="전체 상태"
            value={statusFilter}
            onChange={setStatusFilter}
            options={Object.entries(STATUS_LABEL).map(([key, label]) => ({ value: key, label }))}
          />
        </div>
      </div>

      {/* Table */}
      {filtered.length > 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-3 font-medium text-gray-600">기능코드</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">제목</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">연결된 요구사항</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">상태</th>
                <th className="text-center px-4 py-3 font-medium text-gray-600">TC</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600">작업</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((spec) => (
                <tr
                  key={spec.id}
                  className="hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => navigate(`/projects/${project.projectKey}/specs/${spec.id}`)}
                >
                  <td className="px-4 py-3">
                    <span className="font-mono text-sm text-primary-600 font-medium">
                      {spec.functionCode}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-medium text-gray-900">{spec.title}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-500">
                      {spec.requirement.reqId} — {spec.requirement.title}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded text-sm font-medium ${STATUS_COLOR[spec.status] ?? 'bg-gray-100 text-gray-700'}`}
                    >
                      {STATUS_LABEL[spec.status] ?? spec.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="text-sm text-gray-500">{spec._count.testCases}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(spec.id, spec.title);
                      }}
                      className="text-sm text-red-500 hover:text-red-700 font-medium"
                    >
                      삭제
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center h-48 bg-white border border-gray-200 rounded-xl">
          <FileText className="size-10 text-gray-300 mb-3" />
          <p className="text-gray-500 font-medium">기능 명세가 없습니다.</p>
          <Link
            to={`/projects/${project.projectKey}/specs/new`}
            className="mt-2 text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            첫 번째 명세 작성하기
          </Link>
        </div>
      )}

      {/* Spec Import Modal */}
      {showImportModal && (
        <SpecImportModal
          projectId={project.id}
          onClose={() => setShowImportModal(false)}
          onImported={() => setShowImportModal(false)}
        />
      )}
    </div>
  );
}
